import React from "react";

/**
 * `container_status` (the API's name for `container_fulfillment_stage`) value
 * for a cancelled container order. Matches the labels in OrderTable.jsx.
 */
const CONTAINER_STATUS_CANCELLED = 5;

/**
 * Number of units the order is billed for, taken from the order_summary plan
 * line and falling back to the `pod` column when the summary is unavailable.
 *
 * @param {object} orderDetail - Order detail payload from the API.
 * @returns {number} Expected unit count, 0 when it cannot be determined.
 */
const expectedUnits = (orderDetail) => {
  const summary = Array.isArray(orderDetail?.order_summary)
    ? orderDetail.order_summary
    : [];

  const planQuantity = summary
    .filter((item) => item?.type === "plan")
    .reduce((total, item) => total + (Number(item?.quantity) || 0), 0);

  return planQuantity > 0 ? planQuantity : Number(orderDetail?.pod) || 0;
};

/**
 * Warns that a container order has no unit recorded against it, or fewer units
 * than it is billed for.
 *
 * A container order with no `order_containers` row is invisible to operations:
 * the customer occupies a unit nobody can identify, and that unit is still
 * marked available for sale, so it can be sold twice. This surfaces the gap on
 * the order page so an agent can assign the unit.
 *
 * Only rendered for container orders (`is_container === 1`). Collect & Store
 * and Drop & Store orders are pod-based, have no unit allocation, and are
 * skipped — as is Howdens (`is_container === 2`).
 *
 * Cancelled orders (`container_status === 5`) are also skipped: cancellation
 * soft-deletes the order's allocation rows, so an empty list is the expected
 * end state there rather than a gap to fix.
 *
 * @param {object} props - Component props.
 * @param {object} props.orderDetail - Order detail payload from the API.
 * @returns {JSX.Element|null} The notice, or null when allocation is complete.
 */
export function ContainerAllocationNotice({ orderDetail }) {
  if (Number(orderDetail?.is_container) !== 1) {
    return null;
  }

  if (Number(orderDetail?.container_status) === CONTAINER_STATUS_CANCELLED) {
    return null;
  }

  const rows = Array.isArray(orderDetail?.order_container_details)
    ? orderDetail.order_container_details
    : [];

  const expected = expectedUnits(orderDetail);
  const awaitingUnit = rows.filter((row) => !row?.franchise_container_id).length;
  const shortfall = Math.max(expected - rows.length, 0);

  if (shortfall === 0 && awaitingUnit === 0) {
    return null;
  }

  const messages = [];
  if (rows.length === 0) {
    messages.push(
      `No container unit has been recorded against this order, but it is billed for ${expected} unit${
        expected === 1 ? "" : "s"
      }.`
    );
  } else if (shortfall > 0) {
    messages.push(
      `Only ${rows.length} of ${expected} units are recorded against this order.`
    );
  }
  if (awaitingUnit > 0) {
    messages.push(
      `${awaitingUnit} allocation${
        awaitingUnit === 1 ? " has" : "s have"
      } a site but no unit assigned.`
    );
  }

  return (
    <div
      className="alert alert-custom alert-light-warning fade show mb-5"
      role="alert"
    >
      <div className="alert-icon">
        <i className="flaticon-warning text-warning" />
      </div>
      <div className="alert-text">
        <strong>Container allocation incomplete.</strong> {messages.join(" ")}{" "}
        Until a unit is assigned, the space stays available for sale and may be
        booked by another customer. Assign the unit from the container panel
        below.
      </div>
    </div>
  );
}
